/**
 * AboutScreen: the studio's About page, shared by the Mini App's `/about` and the studio website's
 * phone guest home and About page (shared-client-screens D3, card 2 rows H1 to H14). Props only:
 * each side reads its own studio and hands in Back, the chat row and its slots.
 *
 * A pinned full-bleed photo with a rising sheet over it: the welcome, the story, the guide, the
 * chat row. Each section draws only what the studio filled in. The website's slots:
 *  - `welcome.placement` 'photo': the logo, "Welcome to", the name and the Descriptor centered on
 *    the photo, and the sheet opens on the tagline (H2, H3); 'sheet' is the app's.
 *  - no `onBack`: no back button, it is the front door (H1).
 *  - `afterGuide`: the services and Happening Soon in the sheet (H5, H6; AboutOfferings).
 *  - `footerAction`: the app's Book a Session; the website has none (H9).
 *  - `bottomBar`: the website's bar, with room for it under the sheet (H10).
 */
import { useMemo, type ReactNode } from 'react';
import { FocusedScreenFrame, FocusedScreenLoading, FocusedScreenMessage } from '../../components/booking/FocusedScreenFrame';
import { ChatRow } from '../../components/client/ChatRow';
import styles from './AboutPage.module.css';
import slots from './AboutScreen.module.css';

// ── Icons (mockup SVGs) ──────────────────────────────────────────────────────
const IconPin = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" /><circle cx="12" cy="10" r="3" /></svg>
);
const IconArrowRight = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round"><line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" /></svg>
);

export interface AboutGuideContent {
  name: string;
  role: string | null;
  /** Certifications, already joined. */
  credentials: string | null;
  yearsExperience: number | null;
  photoUrl: string | null;
  bio: string | null;
}

export interface AboutContent {
  name: string;
  /** The one-line description. */
  tagline: string | null;
  location: string | null;
  establishedYear: number | null;
  /** Our story, plain text; paragraphs split on a blank line. */
  story: string | null;
  photoUrl: string | null;
  /** The guide section's heading ("Meet your guide" unless the studio wrote its own). */
  guideHeading: string;
  /** null: the section is left out. */
  guide: AboutGuideContent | null;
}

export type AboutWelcome = { placement: 'sheet' } | { placement: 'photo'; logoUrl: string | null; descriptor: string | null };

export interface AboutScreenProps {
  status: 'loading' | 'error' | 'ready';
  /** ready: the studio. */
  about?: AboutContent;
  /** error: Try again. */
  onRetry: () => void;
  welcome: AboutWelcome;
  /** Absent: no back button. */
  onBack?: () => void;
  /** The chat row: the line under the studio's name and what it opens. */
  chat: { line: string; onChat: () => void };
  afterGuide?: ReactNode;
  footerAction?: { label: string; onClick: () => void };
  bottomBar?: ReactNode;
  testId?: string;
}

/** certifications (already joined server-side) + the years clause the mockup appends. */
function guideCreds(guide: AboutGuideContent): string {
  return [guide.credentials, guide.yearsExperience != null ? `${guide.yearsExperience} yrs experience` : null]
    .filter(Boolean)
    .join(' · ');
}

/** Escape a URL for CSS url("...") interpolation (quotes/backslashes would break out). */
function cssUrl(url: string): string {
  return `url("${url.replace(/[\\"]/g, (c) => `\\${c}`)}")`;
}

/** Split plain-text story on blank lines into paragraphs. */
function storyParagraphs(story: string | null): string[] {
  return (story ?? '')
    .split(/\n\n+/)
    .map((p) => p.trim())
    .filter(Boolean);
}

export function AboutScreen({ status, about, onRetry, welcome, onBack, chat, afterGuide, footerAction, bottomBar, testId = 'about-page' }: AboutScreenProps) {
  const storyParas = useMemo(() => storyParagraphs(about?.story ?? null), [about?.story]);

  let body: ReactNode;
  if (status === 'loading' || (status === 'ready' && !about)) {
    body = <FocusedScreenLoading testId="about-loading" />;
  } else if (status === 'error' || !about) {
    body = (
      <FocusedScreenMessage
        title="Something went wrong"
        message="Please try again."
        action={{ label: 'Try again', onClick: onRetry }}
        testId="about-error"
      />
    );
  } else {
    const tagline = about.tagline?.trim();
    const location = about.location?.trim();
    // Falsy-zero on purpose: a 0/negative year is bad operator input, not "Est. 0".
    const hasYear = typeof about.establishedYear === 'number' && about.establishedYear > 0;
    const hasLocation = !!location;
    const guide = about.guide;
    const creds = guide ? guideCreds(guide) : '';
    const bio = guide?.bio?.trim();
    const showMeta = hasLocation || hasYear;
    const onPhoto = welcome.placement === 'photo';
    const descriptor = welcome.placement === 'photo' ? welcome.descriptor?.trim() : null;

    body = (
      <>
        <div className={styles.scroll}>
          <div
            className={`${styles.hero} ${about.photoUrl ? '' : styles.heroFallback}`}
            style={about.photoUrl ? { backgroundImage: cssUrl(about.photoUrl) } : undefined}
            data-testid="about-hero"
          >
            {welcome.placement === 'photo' && (
              <div className={slots.heroText}>
                {welcome.logoUrl && (
                  <span className={slots.logo}>
                    <img src={welcome.logoUrl} alt="" />
                  </span>
                )}
                <div className={styles.kicker}>Welcome to</div>
                <h1 className={styles.name}>{about.name}</h1>
                {descriptor && <div className={slots.descriptor}>{descriptor}</div>}
              </div>
            )}
          </div>
          <div className={styles.sheet}>
            <div className={styles.grab} aria-hidden="true" />

            {(!onPhoto || tagline || showMeta) && (
              <div className={styles.head}>
                {!onPhoto && <div className={styles.kicker}>Welcome to</div>}
                {!onPhoto && <h1 className={styles.name}>{about.name}</h1>}
                {tagline && <p className={styles.tagline}>{tagline}</p>}
                {showMeta && (
                  <div className={styles.meta} data-testid="about-meta">
                    {hasLocation && <IconPin />}
                    {hasLocation && <span>{location}</span>}
                    {hasLocation && hasYear && <span className={styles.metaSep}>·</span>}
                    {hasYear && <span>Est. {about.establishedYear}</span>}
                  </div>
                )}
              </div>
            )}

            {storyParas.length > 0 && (
              <section className={styles.section} data-testid="about-story">
                <h2 className={styles.sectionTitle}>Our Story</h2>
                {storyParas.map((p, i) => (
                  <p key={i} className={styles.body}>
                    {p}
                  </p>
                ))}
              </section>
            )}

            {guide && (
              <section className={styles.section} data-testid="about-guide">
                <h2 className={styles.sectionTitle}>{about.guideHeading}</h2>
                <div className={styles.guide}>
                  <div
                    className={`${styles.guidePhoto} ${guide.photoUrl ? '' : styles.guidePhotoFallback}`}
                    style={guide.photoUrl ? { backgroundImage: cssUrl(guide.photoUrl) } : undefined}
                    aria-hidden="true"
                  />
                  <div>
                    <div className={styles.guideName}>{guide.name}</div>
                    {guide.role && <div className={styles.guideRole}>{guide.role}</div>}
                    {creds && <div className={styles.guideCreds}>{creds}</div>}
                  </div>
                </div>
                {bio && <p className={styles.body}>{bio}</p>}
              </section>
            )}

            {afterGuide}

            <section className={styles.section}>
              <ChatRow name={about.name} subtitle={chat.line} gradientAvatar onChat={chat.onChat} testId="about-chat" />
            </section>

            {footerAction && (
              <div className={styles.ctaWrap}>
                <button type="button" className={styles.cta} onClick={footerAction.onClick}>
                  {footerAction.label}
                  <IconArrowRight />
                </button>
              </div>
            )}
            {bottomBar && <div className={slots.navSpace} />}
          </div>
        </div>
        {bottomBar}
      </>
    );
  }

  return (
    <FocusedScreenFrame className={styles.page} testId={testId} onBack={onBack}>
      {body}
    </FocusedScreenFrame>
  );
}
